import React, { Component } from 'react';
import { get } from 'lodash';
import FastFormContext, {
  EMPTY_FAST_FORM_STATE,
  type FastFormApi,
  type FastFormApiConfig,
  type FastFormApiOptions,
  type FastFormField,
  type FastFormOwnProps,
  type FastFormState,
  type FastFormSubmitAction,
  type FastFormSubmitCallback,
  type FastFormValues,
} from './fast-form-context';

/*
withFastForm({
 formName: 'comments' || ownProps.formName,
 formInitialValues: {} || ownProps.formInitialValues,
 validate: (fields) => ({errors}),
 resetOnUnmount: false
})
props.fastForm.changeValue('title')(value)
props.fastForm.changeField('title', { active: true })
props.fastForm.resetForm()
props.fastForm.values.title
props.fastForm.fields.title
props.fastForm.errors
props.fastForm.isValid
props.fastForm.isSubmitting
props.fastForm.formCommitTime
props.fastForm.formInitTime
props.fastForm.submit() -> invokes onSubmit on ownProps or cb
props.fastForm.stopSubmit() -> changes isSubmitting state to false
props.fastForm.changeValues(options, { title: 'my' }) -> change value of any form
*/

const normalizeValue = (value: unknown) => {
  if (get(value, 'target.type') === 'checkbox') {
    return get(value, 'target.checked');
  }
  return get(value, 'target.value', value);
};

type Props = {
  submitAction?: FastFormSubmitAction | null;
  onSubmit?: (formName: string, formState: FastFormState) => void;
  children?: React.ReactNode;
};

/** One entry per form, keyed by `formName`. */
type State = Record<string, FastFormState | undefined>;

class FastFormProvider extends Component<Props, State> {
  state: State = {};

  static initEmptyFormState = (
    formFields: readonly string[] = [],
    api: Pick<FastFormApi, 'changeValue' | 'changeField'>,
  ): FastFormState => ({
    values: {},
    errors: {}, // set on value change
    fields: formFields.reduce<Record<string, FastFormField>>((acc, name) => {
      acc[name] = {
        name,
        active: false,
        onChange: api.changeValue(name),
        onFocus: api.changeField(name, { active: true }),
        onBlur: api.changeField(name, { active: false }),
      };
      return acc;
    }, {}),
    isValid: true,
    isSubmitting: false,
    formCommitTime: Date.now(),
    formInitTime: Date.now(),
  });

  static applyValueChangeToState = (
    formState: FastFormState,
    validate: FastFormApiOptions['validate'],
    values: Partial<FastFormValues>,
    setInitialValue?: boolean,
  ): FastFormState => {
    const nextState: FastFormState = {
      ...formState,
      values: {
        ...formState.values,
      },
      fields: {
        ...formState.fields,
      },
    };

    for (const [name, val] of Object.entries(values)) {
      const value = normalizeValue(val);
      const field: FastFormField = { ...nextState.fields[name], name, value };
      if (setInitialValue) {
        field.initialValue = value;
      }
      nextState.values[name] = value;
      nextState.fields[name] = field;
    }

    if (validate) {
      nextState.errors = validate(nextState.values) || {};
    }

    nextState.isValid = Object.keys(nextState.errors).length === 0;
    nextState.formCommitTime = Date.now();
    return nextState;
  };

  static applyFieldChangeToState = (
    formState: FastFormState,
    name: string,
    props: Partial<FastFormField>,
  ): FastFormState => ({
    ...formState,
    fields: {
      ...formState.fields,
      [name]: { ...formState.fields[name], ...props },
    },
  });

  componentDidUpdate(prevProps: Props) {
    if (
      this.props.submitAction &&
      get(prevProps.submitAction, 'timestamp') !== get(this.props.submitAction, 'timestamp')
    ) {
      this.submitForm(this.props.submitAction.formName, undefined, undefined);
    }
  }

  getApi = (
    options: FastFormApiOptions,
    ownProps: FastFormOwnProps,
    config: FastFormApiConfig,
  ): FastFormApi => {
    const formName = (ownProps.formName || options.formName) as string;

    let formState = this.state[formName];
    const api: FastFormApi = {
      // seeded so consumers always see a well-formed state, even when
      // `config.skipInitialize` leaves the form uninitialised
      ...EMPTY_FAST_FORM_STATE,
      submit: (cb?: FastFormSubmitCallback) =>
        this.submitForm(formName, ownProps.onSubmit || cb, this.getApi(options, ownProps, config)),
      stopSubmit: () => {
        const currentFormState = this.state[formName];

        if (!currentFormState) {
          return console.error(`cannot stop submit ${formName} - form doesn't exist`);
        }

        if (!currentFormState.isSubmitting) {
          return;
        }

        this.setState({
          [formName]: {
            ...currentFormState,
            isSubmitting: false,
          },
        });
      },
      changeValue: (name: string) => (value: unknown, cb?: () => void) => {
        const currentFormState = this.state[formName] || formState;
        if (!currentFormState || currentFormState.isSubmitting) {
          return;
        }
        this.setState(
          {
            [formName]: FastFormProvider.applyValueChangeToState(
              currentFormState,
              options.validate,
              { [name]: value },
            ),
          },
          cb,
        );
      },
      changeField: (name: string, props: Partial<FastFormField>) => () => {
        const currentFormState = this.state[formName] || formState;
        if (!currentFormState || currentFormState.isSubmitting) {
          return;
        }
        this.setState({
          [formName]: FastFormProvider.applyFieldChangeToState(currentFormState, name, props),
        });
      },
      changeValues: (
        options: FastFormApiOptions,
        values: Partial<FastFormValues>,
        cb?: (values: FastFormValues) => void,
      ) => {
        const targetFormName = options.formName as string;
        const currentFormState = this.state[targetFormName];

        if (!currentFormState) {
          return console.error(`cannot change ${targetFormName} - form doesn't exist`);
        }

        if (currentFormState.isSubmitting) {
          return;
        }

        const nextState = FastFormProvider.applyValueChangeToState(
          currentFormState,
          options.validate,
          values,
        );
        this.setState({ [targetFormName]: nextState }, () => cb && cb(nextState.values));
      },
      resetForm: () => this.resetForm(formName),
      ...formState,
    };

    if (!formState) {
      formState = FastFormProvider.initEmptyFormState(options.fields, api);
      if (!config.skipInitialize) {
        const initialValues = options.formInitialValues || ownProps.formInitialValues || {};
        formState = FastFormProvider.applyValueChangeToState(
          formState,
          options.validate,
          initialValues,
          true,
        );
        Object.assign(api, formState);
        this.setState({ [formName]: formState });
      }
    }

    return api;
  };

  resetForm = (formName: string) => {
    this.setState({ [formName]: undefined });
  };

  submitForm = (formName: string, cb?: FastFormSubmitCallback, api?: FastFormApi) => {
    const currentFormState = this.state[formName];

    if (!currentFormState) {
      return console.error(`cannot submit ${formName} - form doesn't exist`);
    }

    if (currentFormState.isSubmitting) {
      return;
    }
    const formState: FastFormState = {
      ...currentFormState,
      isSubmitting: true,
    };

    if (cb) {
      cb(formState, api);
    }

    this.setState({ [formName]: formState });
    this.props.onSubmit && this.props.onSubmit(formName, formState); // transmit to redux
  };

  render() {
    return (
      <FastFormContext.Provider value={{ getApi: this.getApi, resetForm: this.resetForm }}>
        {this.props.children}
      </FastFormContext.Provider>
    );
  }
}

export default FastFormProvider;
